import { IOrganization } from '@jumpstart/core';
import AddIcon from '@mui/icons-material/Add';
import AlarmIcon from '@mui/icons-material/Alarm';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAutocomplete } from '@mui/base/AutocompleteUnstyled';
import { collection, getFirestore, query, Query, where } from 'firebase/firestore';
import { Form, Formik, FormikProps } from 'formik';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { Link as RouterLink } from 'react-router-dom';
import * as yup from 'yup';
import { app } from '../../../core/firebase';
import { formStyles } from '../../event-creation/styles';
import { IAvailability, IConfirmation, IScoutRequest } from '../schemas';

const timeMin = moment().add(1, 'hour').startOf('hour');
const timeDefault = moment().add(3, 'hour').startOf('hour');
const timeMax = moment().add(3, 'months');
console.log('MONTH', timeDefault.format('YYYY-MM-DD[T]hh:00'));
// defaultValue="2017-05-24T10:30"
const dialogFormSchema = yup.object({
  id: yup.string().optional(),
  uid: yup.string().required('UID is required'),
  dogId: yup.string().required('Dog ID is required'),
  userName: yup.string().optional(),
  dogName: yup.string().optional(),
  userPhone: yup.string().optional(),
  userEmail: yup.string().required('User email is required'),
  organizationId: yup.string().required('Business ID is required')
    .test(
      'not-unknown',
      'Must provide a valid business',
      (value: string | undefined) => !!value && value.toLowerCase() !== 'unknown'
    ),
  requestId: yup.string().required('Request ID is required'),
  appointmentTime: yup.string().required('Appointment time is required')
    .test(
      'between-min-max-dates',
      'Cannot be before now and 3 months in the future',
      (value: string | undefined) => !!value && moment(value).isBetween(timeMin, timeMax)
    )
    .test(
      'between-min-max-hours',
      'Cannot be before 6:30am or after 6:30pm',
      (value: string | undefined) => {
        console.log('TESTING', value);
        if (!value) {
          return false;
        }
        const date = moment(value);
        const dayStart = date.clone().startOf('day').add(6, 'hour').add(29, 'minutes');
        const dayEnd = date.clone().startOf('day').add(18, 'hour').add(31, 'minutes');
        return date.isBetween(dayStart, dayEnd);
      }
    ),
  created: yup.string(),
  updated: yup.string().optional()
});

export interface IBookingForm {
  request: IScoutRequest;

  onSubmit(confirmation: IConfirmation): void;

  confirmation: IConfirmation;
}

export interface IBookingFormChild extends FormikProps<IConfirmation>, IBookingForm {
}

function BookingFormChild(props: IBookingFormChild) {
  const { request, onSubmit, confirmation, values, setFieldValue, errors, handleChange } = props;
  const organizationsRef = collection(getFirestore(app), 'scout9-organizations');
  const [organizations] = useCollectionData<IOrganization>(query(
    organizationsRef,
    where('industry', 'array-contains', request.offer.subtype)
  ) as Query<IOrganization>);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    focused
  } = useAutocomplete({
    id: 'use-autocomplete-organization',
    options: organizations ? organizations : [],
    getOptionLabel: (option) => option.name,
    clearOnBlur: true,
    onChange: (event, newValue: IOrganization | null) => {
      console.log(newValue);
      if (newValue) {
        setFieldValue('organizationId', newValue.id);
      } else {
        setFieldValue('organizationId', null);
      }
    }
  });

  useEffect(() => {
    setFieldValue('appointmentTime', timeDefault.toISOString());
  }, []);

  return (
    <Form>
      <Grid container direction="column"
            sx={formStyles.root}
      >

        <Grid
          container
          direction="column"
          sx={formStyles.section as any}
        >
          {request.payload.intent && <Typography variant="body1">{request.payload.intent}</Typography>}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
              <Typography>Request Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                container
                direction="column"
                sx={formStyles.section as any}
              >
                {[
                  { key: 'id', label: 'Booking id' },
                  { key: 'uid', label: 'User\'s unique id' },
                  { key: 'dogId', label: 'User\'s dogs id' },
                  { key: 'userName', label: 'User name' },
                  { key: 'dogName', label: 'Dog name' },
                  { key: 'userPhone', label: 'User\'s phone' },
                  { key: 'userEmail', label: 'User\'s email' },
                  { key: 'requestId', label: 'User\'s request id' }
                ].map(({ key, label }) => <TextField
                    key={key}
                    id={key}
                    name={key}
                    value={(values as any)[key]}
                    sx={formStyles.input}
                    style={{ width: '100%' }}
                    variant="outlined"
                    label={label}
                    disabled
                  />
                )}

                <Paper style={{ padding: 24 }}>
                  <Typography variant="body2" color="textSecondary">Request Payload:</Typography>
                  <pre style={{
                    padding: 16,
                    backgroundColor: '#F9F9F9',
                    borderRadius: 8,
                    maxWidth: 420,
                    overflow: 'scroll'
                  }}>{JSON.stringify(request.payload, null, 2)}</pre>
                </Paper>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Grid
            container
            direction="column"
            {...getRootProps()}
          >
            {request.payload.preferredBusiness &&
            <Typography variant="body1">User requested business "{request.payload.preferredBusiness}"</Typography>}
            <TextField
              variant="outlined"
              fullWidth
              autoFocus
              placeholder={values.organizationId}
              label="Business ID"
              sx={formStyles.input}
              error={!!errors.organizationId}
              helperText={errors.organizationId}
              InputLabelProps={{
                ...getInputLabelProps()
              }}
              InputProps={{
                ...getInputProps() as any
              }}
              {...getInputProps() as any}
              inputProps={{
                autoComplete: 'new-password'
              }}
            />
            <List dense disablePadding {...getListboxProps()}>
              {(groupedOptions
                .slice(0, 12) as any)
                .map((option: any, index: number) =>
                  <ListItem
                    style={{ width: 550 }}
                    {...getOptionProps({ option, index })}
                    key={option.id}
                  >
                    <ListItemText
                      id={option.id}
                      primary={`${option.name}`}
                      secondary={(option as any).places ? (option as any).places.formatted_address : ''}
                      secondaryTypographyProps={{ style: { fontSize: 10 } }}
                    />
                  </ListItem>
                )}
              {focused && <ListItem
                style={{ width: 550 }}
                button
                component={RouterLink}
                to="/admin/booking/business-create"
              >
                <ListItemIcon>
                  <AddIcon/>
                </ListItemIcon>
                <ListItemText primary="Add Business"/>
              </ListItem>}
            </List>
          </Grid>

          <TextField
            id="appointmentTime"
            name="appointmentTime"
            label="Appointment Time"
            type="datetime-local"
            // defaultValue={`${timeMin.year()}-${timeMin.month() > 8 ? timeMin.month() + 1 : timeMin.month() + 1}-${timeMin.date()}T${timeMin.hour()}:${timeMin.minutes()}`}
            defaultValue={timeDefault.format('YYYY-MM-DD[T]hh:00')}
            error={!!errors.appointmentTime}
            helperText={!!errors.appointmentTime ? `${errors.appointmentTime}. (${moment(values.appointmentTime)
              .format('MMMM Do YYYY, h:mm a')})` : moment(values.appointmentTime).format('MMMM Do YYYY, h:mm a')}
            // value={values.appointmentTime}
            onChange={(event: any) => {
              setFieldValue('appointmentTime', new Date(event.target.value).toISOString());
            }}
            sx={formStyles.input}
            variant="outlined"
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              step: 900
            }}
          />
          <Paper>
            <List
              dense
              style={{ padding: 24 }}
              subheader={
                <ListSubheader component="div">
                  Preferred Availabilities
                </ListSubheader>
              }
            >
              {(request.payload.availability ? request.payload.availability : []).map((
                availability: IAvailability,
                index: number
                ) =>
                  <ListItem
                    style={{ width: 350 }}
                    key={`option-${index}`}
                  >
                    <ListItemIcon>
                      <AlarmIcon/>
                    </ListItemIcon>
                    <ListItemText
                      primary={moment(availability.date).format('dddd, MMMM Do, YYYY')}
                      secondary={<Typography variant="body2" color="textSecondary">Time of
                        day: <b>{availability.timeOfDay}</b></Typography>}
                      // secondaryTypographyProps={{ style: { fontSize: 10 } }}
                    />
                  </ListItem>
              )}
            </List>
          </Paper>
          <Paper style={{ padding: 24 }}>
            <Typography variant="body2" color="textSecondary">Confirmation Data:</Typography>
            <pre style={{ padding: 16, backgroundColor: '#F9F9F9', borderRadius: 8 }}>{JSON.stringify(
              values,
              null,
              2
            )}</pre>
          </Paper>
          <Grid container justifyContent="center" alignItems="center">
            <Button variant="contained" type="submit" color="primary" style={{ width: '100%' }}>Save</Button>
          </Grid>
        </Grid>
      </Grid>
    </Form>
  );
}

export default function BookingForm(props: IBookingForm) {
  const { onSubmit } = props;
  return (

    <Formik
      initialValues={props.confirmation}
      validationSchema={dialogFormSchema}
      onSubmit={(values: IConfirmation) => {
        if (!values.created) {
          values.created = new Date().toISOString();
        }
        values.updated = new Date().toISOString();
        onSubmit(values);
      }}
    >{(formikProps) => <BookingFormChild {...props} {...formikProps}/>}
    </Formik>
  );
}
